import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "action-menu__button" }

import { nextTick, onMounted, ref } from 'vue'

import { emitter } from '@/services/mitt'

/**
 * This component is a button that opens a dropdown menu of actions (`.action-menu__list`)
 * when clicked. The button component can be customized by either passing
 * the `icon` prop, or using the `#button` slot.
 *
 * The default slot is meant to be used with the `ActionMenuItem` component, which makes
 * it easier to handle the logic and styling of each item.
 *
 * @param position - The aligment of the dropdown list relative to the button.
 * @param icon - An iconfont icon class name that will be used as the button icon.
 *   Note: the button can be customized entirely by using the `#button` slot instead.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'ActionMenu',
  props: {
    position: { default: 'center' },
    icon: { default: 'imore' }
  },
  setup(__props: any) {

const props = __props

const isMenuVisible = ref(false)
const menuVerticalPosition = ref<'top' | 'bottom'>('bottom')

const toggleButtonRef = ref<null | HTMLElement>(null)
const listRef = ref<null | HTMLElement>(null)

function toggleMenu(event: MouseEvent): void {
  if (isMenuVisible.value) {
    hideMenu(event)
  } else {
    showMenu(event)
  }
}

async function showMenu(event: MouseEvent): Promise<void> {
  emitter.emit('showActionMenu')
  isMenuVisible.value = true

  // Check which position to render the menu
  // (above or below the button). Since this function
  // uses the menu's `offsetHeight` in it's logic, we
  // need to make sure the menu has rendered before running
  // the function, hence why we wait vue's `nextTick()`.
  await nextTick()
  checkMenuPosition(event)
}

function hideMenu(event: MouseEvent): void {
  // Don't hide when clicking the toggle button, as that would
  // cause the menu to never show up.
  if (event.target === toggleButtonRef.value) {
    return
  }

  isMenuVisible.value = false
}

/**
 * Check if there's enough position below the button
 * to show the action menu.
 *
 * If it does, render the menu below the button. If
 * it doesn't, render the menu above the button.
 *
 * Because this function checks the height of the menu
 * with its `offsetHeight` property, the menu needs
 * to be rendered before running it.
 */
function checkMenuPosition(event: MouseEvent): void {
  const position = event.y
  const screenHeight = window.innerHeight

  const menuHeight = listRef.value?.offsetHeight || 0
  const bufferSpace = 100

  const availableSpace = screenHeight - position
  const requiredSpace = menuHeight + bufferSpace

  if (requiredSpace >= availableSpace) {
    menuVerticalPosition.value = 'top'
  } else {
    menuVerticalPosition.value = 'bottom'
  }
}

onMounted(() => {
  // Close other instances of ActionMenu when we show this one
  emitter.on('showActionMenu', () => {
    isMenuVisible.value = false
  })

  // Close the menu when the page is scrolled
  emitter.on('appScroll', () => {
    isMenuVisible.value = false
  })
})

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["action-menu", `
      action-menu--${props.position}
      action-menu--${menuVerticalPosition.value}
    `])
  }, [
    _createElementVNode("div", {
      ref_key: "toggleButtonRef",
      ref: toggleButtonRef,
      class: "action-menu__button-wrapper",
      onClick: _withModifiers(toggleMenu, ["stop","prevent"])
    }, [
      _renderSlot(_ctx.$slots, "button", {}, () => [
        _createElementVNode("button", _hoisted_1, [
          _createElementVNode("span", {
            class: _normalizeClass(["iconfont", __props.icon])
          }, null, 2)
        ])
      ])
    ], 512),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (isMenuVisible.value)
          ? _withDirectives((_openBlock(), _createElementBlock("ul", {
              key: 0,
              ref_key: "listRef",
              ref: listRef,
              class: "action-menu__list",
              onClick: hideMenu
            }, [
              _renderSlot(_ctx.$slots, "default")
            ])), [
              [_directive_click_outside, hideMenu]
            ])
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ], 2))
}
}

})